/**
 * Seção de apresentação do eMaster SafeStorage.
 *
 * Renderizada antes do plano de suporte e do investimento: quando a proposta vende um produto, o
 * cliente precisa entender o produto antes de ver o preço. É a mesma ordem do bloco SafePlace.
 *
 * Posicionamento obrigatório (documentação oficial do produto, seção 1): o SafeStorage é sempre
 * apresentado como **eMaster SafeStorage — Powered by AWS**. Nenhuma tecnologia de base do
 * código-fonte aparece aqui, nem deve aparecer: o que o cliente compra é a plataforma gerenciada
 * pela eMaster sobre a infraestrutura da AWS.
 *
 * O diagrama de arquitetura é o desenho oficial do produto, em SVG **embutido no documento** — não
 * referenciado por `src`. A proposta publicada é um HTML autocontido que alguém pode reabrir anos
 * depois, quando o caminho de um asset externo já não responde, e que precisa sobreviver à impressão
 * em PDF, que é como boa parte das propostas é lida. O SVG é gerado a partir do arquivo em
 * `docs/Safestorage` por `.ferramentas/embutir-diagrama-safestorage.mjs`.
 */

import {
  faixaSafeStorage,
  formatarBrl,
  formatarUsd,
  type DocumentoProposta,
} from '@emaster/dominio';

import { cabecalhoSecao } from './componentes.ts';
import { escaparHtml } from './escapar.ts';
import { DIAGRAMA_SAFESTORAGE } from './safestorageDiagrama.ts';
import { CONTAINER, icone } from './tokens.ts';

/** Assets oficiais do produto. Fonte: documentação em `docs/Safestorage`. */
const LOGO = 'https://safestorage.com.br/wp-content/uploads/sites/35/2025/01/Design-sem-nome-7.png';
const VIDEO_ID = 'AYcrPzdaARo';
const VIDEO_URL = `https://youtu.be/${VIDEO_ID}`;
const VIDEO_CAPA = `https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`;
const SITE_PRODUTO = 'https://safestorage.com.br/';

interface Pilar {
  readonly iconeNome: string;
  readonly titulo: string;
  readonly texto: string;
}

/** Pilares de valor. Fonte: documentação do produto, seção 2.2. */
const PILARES: readonly Pilar[] = [
  {
    iconeNome: 'shield-check',
    titulo: 'Soberania e controle total',
    texto:
      'Gestão completa sobre os arquivos da empresa: quem acessou, o que alterou e o que compartilhou para fora, com registro de data, hora e IP.',
  },
  {
    iconeNome: 'database',
    titulo: 'Durabilidade de 11 noves',
    texto:
      'Os dados residem no Amazon S3, projetado para 99,999999999% de durabilidade — a mesma base de armazenamento usada pelas maiores operações do mundo.',
  },
  {
    iconeNome: 'hard-drive',
    titulo: 'Unidade de rede mapeada',
    texto:
      'A equipe continua trabalhando no Windows Explorer ou no Finder, abrindo e salvando arquivos como sempre fez. Sem curva de aprendizado e sem mudar processo.',
  },
  {
    iconeNome: 'wifi',
    titulo: 'Acesso remoto sem VPN',
    texto:
      'Web, desktop e celular com acesso direto e seguro, sem a lentidão e os gargalos de conexões VPN legadas.',
  },
  {
    iconeNome: 'users',
    titulo: 'Integração com Active Directory',
    texto:
      'Provisionamento centralizado de contas e políticas de grupo pelo AD ou LDAP corporativo: um login, uma senha, uma política.',
  },
  {
    iconeNome: 'history',
    titulo: 'Versionamento e lixeira protegida',
    texto:
      'Proteção ativa contra sobrescrita acidental e ransomware. Versões anteriores e itens excluídos são restaurados em poucos cliques.',
  },
  {
    iconeNome: 'link',
    titulo: 'Compartilhamento controlado',
    texto:
      'Links internos e externos com senha obrigatória, prazo de expiração automática e permissão restrita a leitura, envio ou edição.',
  },
  {
    iconeNome: 'lock',
    titulo: 'Segurança e conformidade LGPD',
    texto:
      'Criptografia em trânsito e em repouso, autenticação em dois fatores e trilha de auditoria completa para atender à LGPD.',
  },
  {
    iconeNome: 'headset',
    titulo: 'Suporte especializado eMaster',
    texto:
      'Atendimento consultivo em português, apoio na migração dos dados e monitoramento proativo pelo ecossistema SafeGuardian.',
  },
];

interface LinhaComparativa {
  readonly criterio: string;
  readonly safestorage: string;
  readonly fileServer: string;
  readonly nuvemGenerica: string;
}

/** Matriz comparativa. Fonte: documentação do produto, seção 6. */
const COMPARATIVO: readonly LinhaComparativa[] = [
  {
    criterio: 'Modelo de custo',
    safestorage: 'Faixa previsível por usuários mais armazenamento sob demanda',
    fileServer: 'Hardware, licenças de CAL e manutenção física',
    nuvemGenerica: 'Licença cheia cobrada por usuário',
  },
  {
    criterio: 'Acesso remoto',
    safestorage: 'Nativo por web, desktop e celular, sem VPN',
    fileServer: 'Lento e dependente de VPN',
    nuvemGenerica: 'Nativo',
  },
  {
    criterio: 'Experiência de uso',
    safestorage: 'Unidade mapeada no Windows e no macOS',
    fileServer: 'Unidade de rede local',
    nuvemGenerica: 'Depende de aplicativo proprietário',
  },
  {
    criterio: 'Durabilidade dos dados',
    safestorage: '99,999999999% no Amazon S3',
    fileServer: 'Limitada aos discos e ao RAID local',
    nuvemGenerica: 'Alta',
  },
  {
    criterio: 'Integração com AD e LDAP',
    safestorage: 'Inclusa, com implantação assistida',
    fileServer: 'Nativa, restrita à rede local',
    nuvemGenerica: 'Restrita aos planos corporativos mais caros',
  },
  {
    criterio: 'Suporte técnico',
    safestorage: 'Engenheiros da eMaster, em português',
    fileServer: 'Interno ou terceirizado',
    nuvemGenerica: 'Fila de tickets impessoal',
  },
];

/**
 * Diagrama de arquitetura, com o desenho oficial do produto.
 *
 * O SVG tem 660 px de largura de desenho e rótulos de 12 px. Encolhê-lo para caber num celular
 * tornaria os rótulos ilegíveis, então o desenho mantém largura mínima e a faixa rola na horizontal.
 * `diagrama-rolavel` desfaz isso na impressão, onde não existe rolagem e o excedente seria cortado.
 *
 * A legenda não descreve o desenho de novo: ela traduz a topologia para o que o comprador precisa
 * concluir dela. O texto acessível fica no `title` e no `desc` do próprio SVG.
 */
function diagrama(): string {
  return `<figure class="rounded-3xl border border-gray-100 bg-white p-6 sm:p-8">
                <figcaption class="mb-6 text-center text-sm font-semibold text-brand-blue">Como o SafeStorage se conecta ao seu ambiente</figcaption>
                <div class="diagrama-rolavel overflow-x-auto">
                    <div class="mx-auto min-w-[34rem] max-w-3xl">
                        ${DIAGRAMA_SAFESTORAGE}
                    </div>
                </div>
                <p class="mt-6 border-t border-gray-100 pt-4 text-xs leading-relaxed text-gray-500">
                    O acesso é sempre por HTTPS, em domínio próprio com certificado válido e renovação automática. A plataforma é operada pela eMaster em instância dedicada na AWS, administrada por canal fora da rede pública, e os arquivos residem no Amazon S3 — nenhum dado da sua empresa fica em disco de estação de trabalho.
                </p>
            </figure>`;
}

/** Cartão do plano contratado, com a faixa, a contagem de usuários e a mensalidade da plataforma. */
function cartaoPlano(doc: DocumentoProposta): string {
  const s = doc.premissas.safestorage;
  if (s === null) return '';

  const faixa = faixaSafeStorage(s.plano);
  const valor = doc.resultados.plataformaSafeStorage;

  const emBrl =
    valor.brl === null
      ? ''
      : `<p class="text-sm text-white/70">${escaparHtml(formatarBrl(valor.brl))}</p>`;

  const teto =
    faixa.usuariosAte === null
      ? 'acima de 50 usuários'
      : `até ${String(faixa.usuariosAte)} usuários`;

  return `<div class="rounded-3xl bg-brand-blue p-8 text-white">
                <div class="flex flex-wrap items-start justify-between gap-6">
                    <div>
                        <p class="text-[11px] font-bold uppercase tracking-wider text-brand-accent">Plano contratado</p>
                        <p class="mt-2 text-2xl font-extrabold">${escaparHtml(faixa.nomeComercial)}</p>
                        <p class="mt-1 text-sm text-white/70">Faixa de ${escaparHtml(teto)} · ${String(s.usuarios)} ${s.usuarios === 1 ? 'usuário' : 'usuários'} nesta proposta</p>
                        <p class="mt-4 max-w-md text-sm leading-relaxed text-white/80">${escaparHtml(faixa.indicadoPara)}</p>
                    </div>
                    <div class="text-right">
                        <p class="text-[11px] font-bold uppercase tracking-wider text-white/60">Assinatura mensal</p>
                        <p class="text-3xl font-extrabold text-brand-accent">${escaparHtml(formatarUsd(valor.usd))}</p>
                        ${emBrl}
                    </div>
                </div>
                <div class="mt-6 rounded-xl border border-white/15 bg-white/5 p-4">
                    <p class="text-xs leading-relaxed text-white/80">
                        <span class="font-bold text-white">O armazenamento é cobrado à parte, sob demanda.</span>
                        A assinatura acima cobre a plataforma, o suporte e a gestão. O espaço em disco no Amazon S3 aparece na tabela de investimento e é faturado conforme o volume efetivamente utilizado: nada de pacote fechado nem de espaço ocioso pago.
                    </p>
                </div>
            </div>`;
}

/** Cartão com a capa do vídeo, que abre a apresentação oficial em nova aba. */
function cartaoVideo(): string {
  /*
   * Capa clicável em vez de `iframe` embutido. O `iframe` só existe enquanto há navegador e rede: numa
   * proposta impressa ou salva em PDF — como boa parte é lida — ele vira um retângulo branco. A capa
   * com link continua sendo uma imagem legível no papel e um atalho de um clique na tela.
   */
  return `<a href="${VIDEO_URL}" target="_blank" rel="noopener noreferrer" class="group block overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                <div class="relative">
                    <img src="${VIDEO_CAPA}" alt="Apresentação do eMaster SafeStorage" class="w-full object-cover" data-image-fallback>
                    <span class="absolute inset-0 flex items-center justify-center">
                        <span class="flex h-16 w-16 items-center justify-center rounded-full bg-brand-accent text-white shadow-lg transition-transform group-hover:scale-110">
                            ${icone('play', 'w-7 h-7')}
                        </span>
                    </span>
                </div>
                <div class="p-5">
                    <p class="text-sm font-bold text-brand-blue">Veja a plataforma em funcionamento</p>
                    <p class="mt-1 text-xs leading-relaxed text-gray-500">
                        Apresentação guiada: unidade mapeada, compartilhamento com senha e prazo de expiração, restauração de versões e painel de gestão.
                    </p>
                </div>
            </a>`;
}

/** Matriz comparativa contra as duas alternativas que o cliente costuma ter na mesa. */
function tabelaComparativa(): string {
  const linhas = COMPARATIVO.map(
    (l) => `<tr class="border-t border-gray-100">
                            <th scope="row" class="p-4 text-left align-top text-sm font-semibold text-brand-blue">${escaparHtml(l.criterio)}</th>
                            <td class="border-l border-gray-100 bg-orange-50/40 p-4 align-top text-sm font-medium text-gray-800">${escaparHtml(l.safestorage)}</td>
                            <td class="border-l border-gray-100 p-4 align-top text-sm text-gray-600">${escaparHtml(l.fileServer)}</td>
                            <td class="border-l border-gray-100 p-4 align-top text-sm text-gray-600">${escaparHtml(l.nuvemGenerica)}</td>
                        </tr>`,
  ).join('\n                        ');

  return `<div class="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                <div class="overflow-x-auto">
                    <table class="w-full min-w-[46rem] border-collapse text-left">
                        <thead class="bg-brand-blue text-white">
                            <tr>
                                <th class="p-4 text-sm font-semibold">Critério</th>
                                <th class="border-l border-white/10 p-4 text-sm font-semibold text-brand-accent">eMaster SafeStorage</th>
                                <th class="border-l border-white/10 p-4 text-sm font-semibold">File server local</th>
                                <th class="border-l border-white/10 p-4 text-sm font-semibold">Nuvem pública genérica</th>
                            </tr>
                        </thead>
                        <tbody>
                        ${linhas}
                        </tbody>
                    </table>
                </div>
            </div>`;
}

/**
 * Renderiza a seção do SafeStorage.
 *
 * @returns HTML da seção, ou string vazia quando o produto não faz parte da proposta.
 */
export function secaoSafeStorage(doc: DocumentoProposta): string {
  if (doc.premissas.safestorage === null) return '';

  const pilares = PILARES.map(
    (p) => `<div class="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                        <span class="text-brand-accent">${icone(p.iconeNome, 'w-6 h-6')}</span>
                        <h3 class="mt-3 text-base font-bold text-brand-blue">${escaparHtml(p.titulo)}</h3>
                        <p class="mt-2 text-sm leading-relaxed text-gray-600">${escaparHtml(p.texto)}</p>
                    </div>`,
  ).join('\n                    ');

  return `
    <section id="safestorage" class="scroll-mt-24 border-t border-gray-200 bg-gray-50 py-20">
        <div class="${CONTAINER}">
            ${cabecalhoSecao(
              'eMaster SafeStorage',
              'Seus arquivos corporativos na AWS, com governança',
              [
                'O SafeStorage substitui o file server local por uma plataforma gerenciada de armazenamento, compartilhamento e colaboração de arquivos, construída pela eMaster sobre a infraestrutura da AWS.',
                'Para a sua equipe, nada muda no dia a dia: a unidade de rede continua no mesmo lugar. Para a empresa, muda o que importa — fim do hardware depreciando, acesso remoto sem VPN, trilha de auditoria completa e durabilidade de 11 noves no Amazon S3.',
              ],
            )}

            <div class="mb-10 flex flex-wrap items-center gap-6">
                <img src="${LOGO}" alt="eMaster SafeStorage" class="h-16 w-auto" data-image-fallback>
                <div>
                    <p class="text-sm font-bold text-brand-blue">eMaster SafeStorage — Powered by AWS</p>
                    <a href="${SITE_PRODUTO}" target="_blank" rel="noopener noreferrer" class="text-xs text-brand-accent hover:underline">safestorage.com.br</a>
                </div>
            </div>

            <div class="mb-12 grid gap-8 lg:grid-cols-5">
                <div class="lg:col-span-3">${cartaoPlano(doc)}</div>
                <div class="lg:col-span-2">${cartaoVideo()}</div>
            </div>

            <div class="mb-12">
                <h3 class="mb-6 text-xl font-bold text-brand-blue">O que a plataforma entrega</h3>
                <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    ${pilares}
                </div>
            </div>

            <div class="mb-12">
                <h3 class="mb-6 text-xl font-bold text-brand-blue">Arquitetura da solução</h3>
                ${diagrama()}
            </div>

            <div>
                <h3 class="mb-6 text-xl font-bold text-brand-blue">Comparativo com as alternativas</h3>
                ${tabelaComparativa()}
            </div>
        </div>
    </section>`;
}
